'use client';

import React, { useEffect, useRef, useState, useId } from 'react';

export interface GlassSurfaceProps {
  children?: React.ReactNode;
  width?: number | string;
  height?: number | string;
  borderRadius?: number;
  borderWidth?: number;
  brightness?: number;
  opacity?: number;
  blur?: number;
  displace?: number;
  backgroundOpacity?: number;
  saturation?: number;
  distortionScale?: number;
  redOffset?: number;
  greenOffset?: number;
  blueOffset?: number;
  xChannel?: 'R' | 'G' | 'B';
  yChannel?: 'R' | 'G' | 'B';
  mixBlendMode?:
    | 'normal'
    | 'multiply'
    | 'screen'
    | 'overlay'
    | 'darken'
    | 'lighten'
    | 'color-dodge'
    | 'color-burn'
    | 'hard-light'
    | 'soft-light'
    | 'difference'
    | 'exclusion'
    | 'hue'
    | 'saturation'
    | 'color'
    | 'luminosity'
    | 'plus-darker'
    | 'plus-lighter';
  className?: string;
  style?: React.CSSProperties;
}

const useDarkMode = () => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDark(mediaQuery.matches);

    const handler = (e: MediaQueryListEvent) => setIsDark(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  return isDark;
};

const GlassSurface: React.FC<GlassSurfaceProps> = ({
  children,
  width = 200,
  height = 80,
  borderRadius = 20,
  borderWidth = 0.07,
  brightness = 50,
  opacity = 0.93,
  blur = 11,
  displace = 0,
  backgroundOpacity = 0,
  saturation = 1,
  distortionScale = -180,
  redOffset = 0,
  greenOffset = 10,
  blueOffset = 20,
  xChannel = 'R',
  yChannel = 'G',
  mixBlendMode = 'difference',
  className = '',
  style = {},
}) => {
  const uniqueId = useId().replace(/:/g, '-');
  const filterId = `glass-filter-${uniqueId}`;
  const redGradId = `red-grad-${uniqueId}`;
  const blueGradId = `blue-grad-${uniqueId}`;

  const containerRef = useRef<HTMLDivElement>(null);
  const feImageRef = useRef<SVGFEImageElement>(null);
  const redChannelRef = useRef<SVGFEDisplacementMapElement>(null);
  const greenChannelRef = useRef<SVGFEDisplacementMapElement>(null);
  const blueChannelRef = useRef<SVGFEDisplacementMapElement>(null);
  const gaussianBlurRef = useRef<SVGFEGaussianBlurElement>(null);
  const sizeRef = useRef({
    width: typeof width === 'number' ? width : 400,
    height: typeof height === 'number' ? height : 200,
  });

  const isDarkMode = useDarkMode();
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const generateDisplacementMap = (
    actualWidth = sizeRef.current.width,
    actualHeight = sizeRef.current.height
  ) => {
    const edgeSize = Math.min(actualWidth, actualHeight) * (borderWidth * 0.5);

    const svgContent = `
      <svg viewBox="0 0 ${actualWidth} ${actualHeight}" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="${redGradId}" x1="100%" y1="0%" x2="0%" y2="0%">
            <stop offset="0%" stop-color="#0000"/>
            <stop offset="100%" stop-color="red"/>
          </linearGradient>
          <linearGradient id="${blueGradId}" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stop-color="#0000"/>
            <stop offset="100%" stop-color="blue"/>
          </linearGradient>
        </defs>
        <rect x="0" y="0" width="${actualWidth}" height="${actualHeight}" fill="black"></rect>
        <rect x="0" y="0" width="${actualWidth}" height="${actualHeight}" rx="${borderRadius}" fill="url(#${redGradId})" />
        <rect x="0" y="0" width="${actualWidth}" height="${actualHeight}" rx="${borderRadius}" fill="url(#${blueGradId})" style="mix-blend-mode: ${mixBlendMode}" />
        <rect x="${edgeSize}" y="${edgeSize}" width="${actualWidth - edgeSize * 2}" height="${actualHeight - edgeSize * 2}" rx="${borderRadius}" fill="hsl(0 0% ${brightness}% / ${opacity})" style="filter:blur(${blur}px)" />
      </svg>
    `;

    return `data:image/svg+xml,${encodeURIComponent(svgContent)}`;
  };

  const updateDisplacementMap = (actualWidth?: number, actualHeight?: number) => {
    if (typeof actualWidth === 'number' && typeof actualHeight === 'number') {
      sizeRef.current = { width: actualWidth, height: actualHeight };
    }
    feImageRef.current?.setAttribute('href', generateDisplacementMap(actualWidth, actualHeight));
  };

  useEffect(() => {
    updateDisplacementMap();
    [
      { ref: redChannelRef, offset: redOffset },
      { ref: greenChannelRef, offset: greenOffset },
      { ref: blueChannelRef, offset: blueOffset },
    ].forEach(({ ref, offset }) => {
      if (ref.current) {
        ref.current.setAttribute('scale', (distortionScale + offset).toString());
        ref.current.setAttribute('xChannelSelector', xChannel);
        ref.current.setAttribute('yChannelSelector', yChannel);
      }
    });

    gaussianBlurRef.current?.setAttribute('stdDeviation', displace.toString());
  }, [
    width,
    height,
    borderRadius,
    borderWidth,
    brightness,
    opacity,
    blur,
    displace,
    distortionScale,
    redOffset,
    greenOffset,
    blueOffset,
    xChannel,
    yChannel,
    mixBlendMode,
  ]);

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const nextWidth = entry.contentRect.width || sizeRef.current.width;
      const nextHeight = entry.contentRect.height || sizeRef.current.height;
      window.requestAnimationFrame(() => updateDisplacementMap(nextWidth, nextHeight));
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    window.requestAnimationFrame(() =>
      updateDisplacementMap(sizeRef.current.width, sizeRef.current.height)
    );
  }, [width, height]);

  const supportsSVGFilters = () => {
    // FIX: Add mounted check to prevent SSR error
    if (!isMounted || typeof window === 'undefined') return false;

    const isWebkit = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    const isFirefox = /Firefox/.test(navigator.userAgent);

    if (isWebkit || isFirefox) {
      return false;
    }

    const div = document.createElement('div');
    div.style.backdropFilter = `url(#${filterId})`;
    return div.style.backdropFilter !== '';
  };

  const supportsBackdropFilter = () => {
    if (typeof window === 'undefined') return false;
    return CSS.supports('backdrop-filter', 'blur(10px)');
  };

  const getContainerStyles = (): React.CSSProperties => {
    const baseStyles: React.CSSProperties = {
      ...style,
      width: typeof width === 'number' ? `${width}px` : width,
      height: typeof height === 'number' ? `${height}px` : height,
      borderRadius: `${borderRadius}px`,
      '--glass-frost': backgroundOpacity,
      '--glass-saturation': saturation,
    } as React.CSSProperties;

    const svgSupported = supportsSVGFilters();
    const backdropFilterSupported = supportsBackdropFilter();

    if (svgSupported) {
      return {
        ...baseStyles,
        background: isDarkMode
          ? `hsl(0 0% 0% / ${backgroundOpacity})`
          : `hsl(0 0% 100% / ${backgroundOpacity})`,
        backdropFilter: `url(#${filterId}) saturate(${saturation})`,
        boxShadow: isDarkMode
          ? `0 0 2px 1px color-mix(in oklch, white, transparent 65%) inset,
             0 0 10px 4px color-mix(in oklch, white, transparent 85%) inset,
             0px 4px 16px rgba(17, 17, 26, 0.05),
             0px 8px 24px rgba(17, 17, 26, 0.05),
             0px 16px 56px rgba(17, 17, 26, 0.05),
             0px 4px 16px rgba(17, 17, 26, 0.05) inset,
             0px 8px 24px rgba(17, 17, 26, 0.05) inset,
             0px 16px 56px rgba(17, 17, 26, 0.05) inset`
          : `0 0 2px 1px color-mix(in oklch, black, transparent 85%) inset,
             0 0 10px 4px color-mix(in oklch, black, transparent 90%) inset,
             0px 4px 16px rgba(17, 17, 26, 0.05),
             0px 8px 24px rgba(17, 17, 26, 0.05),
             0px 16px 56px rgba(17, 17, 26, 0.05),
             0px 4px 16px rgba(17, 17, 26, 0.05) inset,
             0px 8px 24px rgba(17, 17, 26, 0.05) inset,
             0px 16px 56px rgba(17, 17, 26, 0.05) inset`,
      };
    } else {
      if (isDarkMode) {
        if (!backdropFilterSupported) {
          return {
            ...baseStyles,
            background: 'rgba(0, 0, 0, 0.4)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: `inset 0 1px 0 0 rgba(255, 255, 255, 0.2),
                        inset 0 -1px 0 0 rgba(255, 255, 255, 0.1)`,
          };
        } else {
          return {
            ...baseStyles,
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(12px) saturate(1.8) brightness(1.2)',
            WebkitBackdropFilter: 'blur(12px) saturate(1.8) brightness(1.2)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: `inset 0 1px 0 0 rgba(255, 255, 255, 0.2),
                        inset 0 -1px 0 0 rgba(255, 255, 255, 0.1)`,
          };
        }
      } else {
        if (!backdropFilterSupported) {
          return {
            ...baseStyles,
            background: 'rgba(255, 255, 255, 0.4)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: `inset 0 1px 0 0 rgba(255, 255, 255, 0.5),
                        inset 0 -1px 0 0 rgba(255, 255, 255, 0.3)`,
          };
        } else {
          return {
            ...baseStyles,
            background: 'rgba(255, 255, 255, 0.25)',
            backdropFilter: 'blur(12px) saturate(1.8) brightness(1.1)',
            WebkitBackdropFilter: 'blur(12px) saturate(1.8) brightness(1.1)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: `0 8px 32px 0 rgba(31, 38, 135, 0.2),
                        0 2px 16px 0 rgba(31, 38, 135, 0.1),
                        inset 0 1px 0 0 rgba(255, 255, 255, 0.4),
                        inset 0 -1px 0 0 rgba(255, 255, 255, 0.2)`,
          };
        }
      }
    }
  };

  const glassSurfaceClasses =
    'relative flex items-center justify-center overflow-hidden transition-opacity duration-[260ms] ease-out';

  const focusVisibleClasses = isDarkMode
    ? 'focus-visible:outline-2 focus-visible:outline-[#0A84FF] focus-visible:outline-offset-2'
    : 'focus-visible:outline-2 focus-visible:outline-[#007AFF] focus-visible:outline-offset-2';

  return (
    <div data-component-id="src/components/ui/GlassSurface.tsx:314:4" data-component-path="src/components/ui/GlassSurface.tsx" data-component-line="314" data-component-end-line="404" data-component-file="GlassSurface.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
      ref={containerRef}
      suppressHydrationWarning
      className={`${glassSurfaceClasses} ${focusVisibleClasses} ${className}`}
      style={getContainerStyles()}
    >
      <svg data-component-id="src/components/ui/GlassSurface.tsx:320:6" data-component-path="src/components/ui/GlassSurface.tsx" data-component-line="320" data-component-end-line="399" data-component-file="GlassSurface.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-full%20h-full%20pointer-events-none%20absolute%20inset-0%20opacity-0%20-z-10%22%7D"
        className="w-full h-full pointer-events-none absolute inset-0 opacity-0 -z-10"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs data-component-id="src/components/ui/GlassSurface.tsx:324:8" data-component-path="src/components/ui/GlassSurface.tsx" data-component-line="324" data-component-end-line="398" data-component-file="GlassSurface.tsx" data-component-name="defs" data-component-content="%7B%22elementName%22%3A%22defs%22%7D">
          <filter data-component-id="src/components/ui/GlassSurface.tsx:325:10" data-component-path="src/components/ui/GlassSurface.tsx" data-component-line="325" data-component-end-line="397" data-component-file="GlassSurface.tsx" data-component-name="filter" data-component-content="%7B%22elementName%22%3A%22filter%22%2C%22id%22%3A%22%5Bvar%3AfilterId%5D%22%7D"
            id={filterId}
            colorInterpolationFilters="sRGB"
            x="0%"
            y="0%"
            width="100%"
            height="100%"
          >
            <feImage data-component-id="src/components/ui/GlassSurface.tsx:333:12" data-component-path="src/components/ui/GlassSurface.tsx" data-component-line="333" data-component-end-line="341" data-component-file="GlassSurface.tsx" data-component-name="feImage" data-component-content="%7B%22elementName%22%3A%22feImage%22%7D"
              ref={feImageRef}
              x="0"
              y="0"
              width="100%"
              height="100%"
              preserveAspectRatio="none"
              result="map"
            />

            <feDisplacementMap data-component-id="src/components/ui/GlassSurface.tsx:343:12" data-component-path="src/components/ui/GlassSurface.tsx" data-component-line="343" data-component-end-line="349" data-component-file="GlassSurface.tsx" data-component-name="feDisplacementMap" data-component-content="%7B%22elementName%22%3A%22feDisplacementMap%22%2C%22id%22%3A%22redchannel%22%7D"
              ref={redChannelRef}
              in="SourceGraphic"
              in2="map"
              id="redchannel"
              result="dispRed"
            />
            <feColorMatrix data-component-id="src/components/ui/GlassSurface.tsx:350:12" data-component-path="src/components/ui/GlassSurface.tsx" data-component-line="350" data-component-end-line="358" data-component-file="GlassSurface.tsx" data-component-name="feColorMatrix" data-component-content="%7B%22elementName%22%3A%22feColorMatrix%22%2C%22type%22%3A%22matrix%22%7D"
              in="dispRed"
              type="matrix"
              values="1 0 0 0 0
                      0 0 0 0 0
                      0 0 0 0 0
                      0 0 0 1 0"
              result="red"
            />

            <feDisplacementMap data-component-id="src/components/ui/GlassSurface.tsx:360:12" data-component-path="src/components/ui/GlassSurface.tsx" data-component-line="360" data-component-end-line="366" data-component-file="GlassSurface.tsx" data-component-name="feDisplacementMap" data-component-content="%7B%22elementName%22%3A%22feDisplacementMap%22%2C%22id%22%3A%22greenchannel%22%7D"
              ref={greenChannelRef}
              in="SourceGraphic"
              in2="map"
              id="greenchannel"
              result="dispGreen"
            />
            <feColorMatrix data-component-id="src/components/ui/GlassSurface.tsx:367:12" data-component-path="src/components/ui/GlassSurface.tsx" data-component-line="367" data-component-end-line="375" data-component-file="GlassSurface.tsx" data-component-name="feColorMatrix" data-component-content="%7B%22elementName%22%3A%22feColorMatrix%22%2C%22type%22%3A%22matrix%22%7D"
              in="dispGreen"
              type="matrix"
              values="0 0 0 0 0
                      0 1 0 0 0
                      0 0 0 0 0
                      0 0 0 1 0"
              result="green"
            />

            <feDisplacementMap data-component-id="src/components/ui/GlassSurface.tsx:377:12" data-component-path="src/components/ui/GlassSurface.tsx" data-component-line="377" data-component-end-line="383" data-component-file="GlassSurface.tsx" data-component-name="feDisplacementMap" data-component-content="%7B%22elementName%22%3A%22feDisplacementMap%22%2C%22id%22%3A%22bluechannel%22%7D"
              ref={blueChannelRef}
              in="SourceGraphic"
              in2="map"
              id="bluechannel"
              result="dispBlue"
            />
            <feColorMatrix data-component-id="src/components/ui/GlassSurface.tsx:384:12" data-component-path="src/components/ui/GlassSurface.tsx" data-component-line="384" data-component-end-line="392" data-component-file="GlassSurface.tsx" data-component-name="feColorMatrix" data-component-content="%7B%22elementName%22%3A%22feColorMatrix%22%2C%22type%22%3A%22matrix%22%7D"
              in="dispBlue"
              type="matrix"
              values="0 0 0 0 0
                      0 0 0 0 0
                      0 0 1 0 0
                      0 0 0 1 0"
              result="blue"
            />

            <feBlend data-component-id="src/components/ui/GlassSurface.tsx:394:12" data-component-path="src/components/ui/GlassSurface.tsx" data-component-line="394" data-component-end-line="394" data-component-file="GlassSurface.tsx" data-component-name="feBlend" data-component-content="%7B%22elementName%22%3A%22feBlend%22%7D" in="red" in2="green" mode="screen" result="rg" />
            <feBlend data-component-id="src/components/ui/GlassSurface.tsx:395:12" data-component-path="src/components/ui/GlassSurface.tsx" data-component-line="395" data-component-end-line="395" data-component-file="GlassSurface.tsx" data-component-name="feBlend" data-component-content="%7B%22elementName%22%3A%22feBlend%22%7D" in="rg" in2="blue" mode="screen" result="output" />
            <feGaussianBlur data-component-id="src/components/ui/GlassSurface.tsx:396:12" data-component-path="src/components/ui/GlassSurface.tsx" data-component-line="396" data-component-end-line="396" data-component-file="GlassSurface.tsx" data-component-name="feGaussianBlur" data-component-content="%7B%22elementName%22%3A%22feGaussianBlur%22%7D" ref={gaussianBlurRef} in="output" stdDeviation="0.7" />
          </filter>
        </defs>
      </svg>

      <div data-component-id="src/components/ui/GlassSurface.tsx:401:6" data-component-path="src/components/ui/GlassSurface.tsx" data-component-line="401" data-component-end-line="403" data-component-file="GlassSurface.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20h-full%20flex%20items-center%20justify-center%20p-2%20rounded-%5Binherit%5D%20relative%20z-10%22%7D" className="w-full h-full flex items-center justify-center p-2 rounded-[inherit] relative z-10">
        {children}
      </div>
    </div>
  );
};

export default GlassSurface;
